import React, { useMemo, useRef, useState } from "react";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const newId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 7)}`;

const toPositive = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const STAGE_PRESETS = [
  { id: "stage", label: "Stage", w: 40, h: 12 },
  { id: "mandap", label: "Mandap / Altar", w: 36, h: 14 },
  { id: "runway", label: "Runway", w: 14, h: 52 },
  { id: "dance", label: "Dance floor", w: 28, h: 28 },
  { id: "platform", label: "Head platform", w: 48, h: 10 },
];

const rowBlockSize = (seatCount) => ({
  // Drag-bound estimate; visual width follows seat count (max-content).
  w: Math.min(92, Math.max(24, 10 + seatCount * 4.2)),
  h: 18,
});

const tableBlockSize = (seatCount) => {
  const outerPct = Math.min(36, Math.max(18, 12 + seatCount * 1.15));
  return { w: outerPct, h: outerPct };
};

const SNAP_PCT = 1.15;

const near = (a, b, tol = SNAP_PCT) => Math.abs(a - b) <= tol;

/**
 * Snap drag position to peers / stage (edges, centers, equal spacing).
 * Returns snapped x/y plus guide lines for visual feedback.
 */
const snapBlockPosition = ({
  x,
  y,
  w,
  h,
  kind,
  id,
  isStage,
  blocks,
  stage,
}) => {
  const guides = [];
  let nx = x;
  let ny = y;
  const centerX = x + w / 2;
  const centerY = y + h / 2;

  const vTargets = []; // vertical lines (x positions)
  const hTargets = []; // horizontal lines (y positions)

  if (stage && !isStage) {
    const sx = Number(stage.x) || 0;
    const sy = Number(stage.y) || 0;
    const sw = Number(stage.w) || 0;
    const sh = Number(stage.h) || 0;
    vTargets.push(
      { pos: sx, label: "stage-left" },
      { pos: sx + sw / 2, label: "stage-center", center: true },
      { pos: sx + sw, label: "stage-right" }
    );
    hTargets.push(
      { pos: sy, label: "stage-top" },
      { pos: sy + sh / 2, label: "stage-mid", center: true },
      { pos: sy + sh, label: "stage-bottom" }
    );
  }

  const peers = (blocks || []).filter((b) => b && (isStage || b.id !== id));

  peers.forEach((b) => {
    const bx = Number(b.x) || 0;
    const by = Number(b.y) || 0;
    const bw = Number(b.w) || w;
    const bh = Number(b.h) || h;
    vTargets.push(
      { pos: bx, label: "edge" },
      { pos: bx + bw / 2, label: "center", center: true },
      { pos: bx + bw, label: "edge" }
    );
    hTargets.push(
      { pos: by, label: "edge" },
      { pos: by + bh / 2, label: "center", center: true },
      { pos: by + bh, label: "edge" }
    );
  });

  // Prefer center-to-center with stage, then edge align
  let bestVX = null;
  let bestVY = null;

  vTargets.forEach((t) => {
    const candidates = t.center
      ? [{ value: t.pos - w / 2, guide: t.pos }]
      : [
          { value: t.pos, guide: t.pos },
          { value: t.pos - w, guide: t.pos },
        ];
    candidates.forEach((c) => {
      const dist = Math.abs(nx - c.value);
      if (dist <= SNAP_PCT && (bestVX == null || dist < bestVX.dist)) {
        bestVX = { dist, x: c.value, guide: c.guide, center: Boolean(t.center) };
      }
      // Also snap when current center is near target
      if (t.center && near(centerX, t.pos)) {
        const d = Math.abs(centerX - t.pos);
        if (bestVX == null || d < bestVX.dist) {
          bestVX = { dist: d, x: t.pos - w / 2, guide: t.pos, center: true };
        }
      }
    });
  });

  hTargets.forEach((t) => {
    const candidates = t.center
      ? [{ value: t.pos - h / 2, guide: t.pos }]
      : [
          { value: t.pos, guide: t.pos },
          { value: t.pos - h, guide: t.pos },
        ];
    candidates.forEach((c) => {
      const dist = Math.abs(ny - c.value);
      if (dist <= SNAP_PCT && (bestVY == null || dist < bestVY.dist)) {
        bestVY = { dist, y: c.value, guide: c.guide, center: Boolean(t.center) };
      }
      if (t.center && near(centerY, t.pos)) {
        const d = Math.abs(centerY - t.pos);
        if (bestVY == null || d < bestVY.dist) {
          bestVY = { dist: d, y: t.pos - h / 2, guide: t.pos, center: true };
        }
      }
    });
  });

  // Equal vertical spacing among same-kind peers (rows under rows, tables under tables)
  if (!isStage && kind) {
    const sameKind = peers
      .filter((b) => b.kind === kind)
      .map((b) => ({
        y: Number(b.y) || 0,
        h: Number(b.h) || h,
        mid: (Number(b.y) || 0) + (Number(b.h) || h) / 2,
      }))
      .sort((a, b) => a.y - b.y);

    if (sameKind.length >= 1) {
      // Midpoint between two neighbors
      for (let i = 0; i < sameKind.length - 1; i += 1) {
        const mid = (sameKind[i].y + sameKind[i + 1].y) / 2;
        if (near(ny, mid)) {
          const d = Math.abs(ny - mid);
          if (bestVY == null || d < bestVY.dist) {
            bestVY = { dist: d, y: mid, guide: mid, equal: true };
          }
        }
      }

      // Match gap used between consecutive peers
      if (sameKind.length >= 2) {
        const gaps = [];
        for (let i = 0; i < sameKind.length - 1; i += 1) {
          gaps.push(sameKind[i + 1].y - sameKind[i].y);
        }
        const avgGap =
          gaps.reduce((s, g) => s + g, 0) / Math.max(gaps.length, 1);

        sameKind.forEach((peer) => {
          const below = peer.y + avgGap;
          const above = peer.y - avgGap;
          [below, above].forEach((candidate) => {
            if (near(ny, candidate, SNAP_PCT * 1.2)) {
              const d = Math.abs(ny - candidate);
              if (bestVY == null || d < bestVY.dist) {
                bestVY = {
                  dist: d,
                  y: candidate,
                  guide: candidate,
                  equal: true,
                };
              }
            }
          });
        });
      }
    }

    // Equal horizontal spacing for same-kind (tables in a row)
    const sameKindX = peers
      .filter((b) => b.kind === kind)
      .map((b) => Number(b.x) || 0)
      .sort((a, b) => a - b);
    if (sameKindX.length >= 2) {
      const gaps = [];
      for (let i = 0; i < sameKindX.length - 1; i += 1) {
        gaps.push(sameKindX[i + 1] - sameKindX[i]);
      }
      const avgGap = gaps.reduce((s, g) => s + g, 0) / gaps.length;
      sameKindX.forEach((px) => {
        [px + avgGap, px - avgGap].forEach((candidate) => {
          if (near(nx, candidate, SNAP_PCT * 1.2)) {
            const d = Math.abs(nx - candidate);
            if (bestVX == null || d < bestVX.dist) {
              bestVX = {
                dist: d,
                x: candidate,
                guide: candidate,
                equal: true,
              };
            }
          }
        });
      });
    }
  }

  if (bestVX) {
    nx = bestVX.x;
    guides.push({
      axis: "v",
      pos: bestVX.guide,
      kind: bestVX.equal ? "equal" : bestVX.center ? "center" : "edge",
    });
  }
  if (bestVY) {
    ny = bestVY.y;
    guides.push({
      axis: "h",
      pos: bestVY.guide,
      kind: bestVY.equal ? "equal" : bestVY.center ? "center" : "edge",
    });
  }

  return {
    x: clamp(nx, 0, 100 - w),
    y: clamp(ny, 0, 100 - h),
    guides,
  };
};

/**
 * Custom seating floor builder.
 * Classic row/table visuals, zoom, stage presets, per-block remove.
 */
const CustomSeatingBuilder = ({
  initialStyle = null,
  layoutMeta,
  rsvpPeopleCount = 0,
  onCancel,
  onSave,
  onSaveDraft,
  saving = false,
}) => {
  const floorRef = useRef(null);
  const dragRef = useRef(null);

  const maxRows = Math.max(toPositive(layoutMeta?.rows), 0);
  const maxTables = Math.max(toPositive(layoutMeta?.tables), 0);
  const defaultSeatsPerRow = Math.max(toPositive(layoutMeta?.seatsPerRow), 1);
  const defaultSeatsPerTable = Math.max(
    toPositive(layoutMeta?.seatsPerTable),
    1
  );

  const allowRows = maxRows > 0;
  const allowTables = maxTables > 0;

  const [name, setName] = useState(
    initialStyle?.name || "Custom seating style"
  );
  const [stageType, setStageType] = useState(() => {
    const label = initialStyle?.stage?.label || "Stage / Mandap";
    const match = STAGE_PRESETS.find(
      (p) =>
        p.label === label ||
        label.toLowerCase().includes(p.id) ||
        (p.id === "mandap" && /mandap|altar/i.test(label))
    );
    return match?.id || "stage";
  });
  const [stage, setStage] = useState(() => {
    if (initialStyle?.stage) return initialStyle.stage;
    const preset = STAGE_PRESETS[0];
    return {
      x: 30,
      y: 4,
      w: preset.w,
      h: preset.h,
      label: preset.label,
      type: preset.id,
    };
  });
  const [blocks, setBlocks] = useState(() =>
    Array.isArray(initialStyle?.blocks)
      ? initialStyle.blocks.map((b) => {
          const seatCount = Math.max(
            1,
            Number(b.seatCount) ||
              (b.kind === "table" ? defaultSeatsPerTable : defaultSeatsPerRow)
          );
          const size =
            b.kind === "table"
              ? tableBlockSize(seatCount)
              : rowBlockSize(seatCount);
          return {
            ...b,
            seatCount,
            w: b.w || size.w,
            h: b.h || size.h,
          };
        })
      : []
  );
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");
  const [pendingRowSeats, setPendingRowSeats] = useState(defaultSeatsPerRow);
  const [pendingTableSeats, setPendingTableSeats] = useState(
    defaultSeatsPerTable
  );
  const [zoom, setZoom] = useState(1);
  const [alignGuides, setAlignGuides] = useState([]);

  const usedRowLocations = useMemo(
    () =>
      new Set(
        blocks.filter((b) => b.kind === "row").map((b) => Number(b.location))
      ),
    [blocks]
  );
  const usedTableLocations = useMemo(
    () =>
      new Set(
        blocks.filter((b) => b.kind === "table").map((b) => Number(b.location))
      ),
    [blocks]
  );

  const nextRowLocation = () => {
    for (let i = 1; i <= Math.max(maxRows, usedRowLocations.size + 1); i += 1) {
      if (!usedRowLocations.has(i)) return i;
    }
    return usedRowLocations.size + 1;
  };

  const nextTableLocation = () => {
    for (
      let i = 1;
      i <= Math.max(maxTables, usedTableLocations.size + 1);
      i += 1
    ) {
      if (!usedTableLocations.has(i)) return i;
    }
    return usedTableLocations.size + 1;
  };

  const placedSeats = blocks.reduce(
    (sum, b) => sum + Math.max(1, Number(b.seatCount) || 1),
    0
  );
  const occasionCap =
    maxRows * defaultSeatsPerRow + maxTables * defaultSeatsPerTable;
  const effectiveNeed = Math.max(rsvpPeopleCount, 0);
  const selectedBlock =
    selectedId && selectedId !== "stage"
      ? blocks.find((b) => b.id === selectedId)
      : null;

  const applyStagePreset = (typeId, { keepPosition = true } = {}) => {
    const preset =
      STAGE_PRESETS.find((p) => p.id === typeId) || STAGE_PRESETS[0];
    setStageType(preset.id);
    setStage((prev) => {
      const x = keepPosition && prev ? prev.x : 30;
      const y = keepPosition && prev ? prev.y : 4;
      return {
        x: clamp(x, 0, 100 - preset.w),
        y: clamp(y, 0, 100 - preset.h),
        w: preset.w,
        h: preset.h,
        label: preset.label,
        type: preset.id,
      };
    });
    setSelectedId("stage");
  };

  const ensureStage = () => {
    if (stage) {
      applyStagePreset(stageType || "stage");
      return;
    }
    applyStagePreset(stageType || "stage", { keepPosition: false });
  };

  const addRowBlock = () => {
    setError("");
    if (!allowRows) {
      setError("Set number of rows in Capacity first.");
      return;
    }
    if (usedRowLocations.size >= maxRows) {
      setError(
        `All ${maxRows} row slot${maxRows === 1 ? "" : "s"} are already used.`
      );
      return;
    }
    const location = nextRowLocation();
    const seatCount = Math.max(1, Number(pendingRowSeats) || defaultSeatsPerRow);
    const size = rowBlockSize(seatCount);
    const id = newId("block");
    setBlocks((prev) => [
      ...prev,
      {
        id,
        kind: "row",
        location,
        seatCount,
        x: 8 + (prev.length % 3) * 8,
        y: 22 + Math.floor(prev.length / 3) * 16,
        w: size.w,
        h: size.h,
      },
    ]);
    setSelectedId(id);
  };

  const addTableBlock = () => {
    setError("");
    if (!allowTables) {
      setError("Set number of tables in Capacity first.");
      return;
    }
    if (usedTableLocations.size >= maxTables) {
      setError(
        `All ${maxTables} table slot${maxTables === 1 ? "" : "s"} are already used.`
      );
      return;
    }
    const location = nextTableLocation();
    const seatCount = Math.max(
      1,
      Number(pendingTableSeats) || defaultSeatsPerTable
    );
    const size = tableBlockSize(seatCount);
    const id = newId("block");
    setBlocks((prev) => [
      ...prev,
      {
        id,
        kind: "table",
        location,
        seatCount,
        x: 16 + (prev.length % 4) * 10,
        y: 40 + Math.floor(prev.length / 4) * 16,
        w: size.w,
        h: size.h,
      },
    ]);
    setSelectedId(id);
  };

  const updateSelectedSeatCount = (value) => {
    if (!selectedBlock) return;
    const seatCount = Math.max(1, Math.min(60, Number(value) || 1));
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== selectedBlock.id) return b;
        const size =
          b.kind === "table"
            ? tableBlockSize(seatCount)
            : rowBlockSize(seatCount);
        return { ...b, seatCount, w: size.w, h: size.h };
      })
    );
  };

  const removeBlock = (id) => {
    if (id === "stage") {
      setStage(null);
      setSelectedId(null);
      return;
    }
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setSelectedId((prev) => (prev === id ? null : prev));
  };

  const removeSelected = () => {
    if (!selectedId) return;
    removeBlock(selectedId);
  };

  const adjustZoom = (delta) => {
    setZoom((prev) => clamp(Number((prev + delta).toFixed(2)), 0.55, 1.8));
  };

  const onFloorWheel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    adjustZoom(event.deltaY > 0 ? -0.08 : 0.08);
  };

  const onPointerDownBlock = (event, target) => {
    event.preventDefault();
    event.stopPropagation();
    const floor = floorRef.current;
    if (!floor) return;
    const rect = floor.getBoundingClientRect();
    const isStage = target === "stage";
    const block = isStage ? stage : blocks.find((b) => b.id === target);
    if (!block) return;
    setSelectedId(isStage ? "stage" : target);
    dragRef.current = {
      target,
      isStage,
      offsetX: ((event.clientX - rect.left) / rect.width) * 100 - block.x,
      offsetY: ((event.clientY - rect.top) / rect.height) * 100 - block.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    const floor = floorRef.current;
    if (!drag || !floor) return;
    const rect = floor.getBoundingClientRect();
    const rawX = ((event.clientX - rect.left) / rect.width) * 100 - drag.offsetX;
    const rawY = ((event.clientY - rect.top) / rect.height) * 100 - drag.offsetY;

    if (drag.isStage) {
      const w = Number(stage?.w) || 40;
      const h = Number(stage?.h) || 12;
      const snapped = snapBlockPosition({
        x: rawX,
        y: rawY,
        w,
        h,
        kind: "stage",
        id: "stage",
        isStage: true,
        blocks,
        stage: null,
      });
      setAlignGuides(snapped.guides);
      setStage((prev) =>
        prev
          ? {
              ...prev,
              x: snapped.x,
              y: snapped.y,
            }
          : prev
      );
      return;
    }

    const moving = blocks.find((b) => b.id === drag.target);
    if (!moving) return;
    const snapped = snapBlockPosition({
      x: rawX,
      y: rawY,
      w: Number(moving.w) || 20,
      h: Number(moving.h) || 12,
      kind: moving.kind,
      id: moving.id,
      isStage: false,
      blocks,
      stage,
    });
    setAlignGuides(snapped.guides);
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === drag.target
          ? {
              ...b,
              x: snapped.x,
              y: snapped.y,
            }
          : b
      )
    );
  };

  const onPointerUp = () => {
    dragRef.current = null;
    setAlignGuides([]);
  };

  const buildPayload = (asDraft) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) {
      setError("Give your style a name.");
      return null;
    }
    if (!blocks.length) {
      setError("Place at least one row or table block.");
      return null;
    }
    setError("");
    const hasRow = blocks.some((b) => b.kind === "row");
    const hasTable = blocks.some((b) => b.kind === "table");
    const appliesTo =
      hasRow && hasTable ? "both" : hasTable ? "table" : "row";

    return {
      id: initialStyle?.id || newId("custom"),
      name: trimmed,
      isCustom: true,
      appliesTo,
      status: asDraft ? "draft" : "published",
      tag: asDraft ? "Draft" : "Custom",
      blurb: asDraft
        ? "Draft floor plan — not finalized yet."
        : effectiveNeed > placedSeats
          ? `Floor plan with ${placedSeats} seats (${effectiveNeed} RSVP’d guests).`
          : "Custom floor plan with per-block seat counts.",
      stage: stage
        ? {
            ...stage,
            type: stage.type || stageType,
            label: stage.label,
          }
        : null,
      blocks: blocks.map((b) => ({
        ...b,
        seatCount: Math.max(1, Number(b.seatCount) || 1),
      })),
      _rsvpShortfall:
        effectiveNeed > placedSeats ? effectiveNeed - placedSeats : 0,
    };
  };

  const handleSave = () => {
    const payload = buildPayload(false);
    if (!payload) return;
    onSave(payload);
  };

  const handleSaveDraft = () => {
    const payload = buildPayload(true);
    if (!payload) return;
    if (onSaveDraft) onSaveDraft(payload);
    else onSave(payload, { asDraft: true });
  };

  const renderSeatPreview = (block) => {
    const count = Math.max(1, Number(block.seatCount) || 1);

    if (block.kind === "row") {
      return (
        <div className="sab-builder-classic-row">
          <span className="sab-builder-classic-tag">Row {block.location}</span>
          <div className="sab-builder-classic-seats sa-row-seats">
            {Array.from({ length: count }, (_, i) => (
              <span key={i} className="sab-builder-classic-seat sa-layout-seat is-empty">
                {i + 1}
              </span>
            ))}
          </div>
        </div>
      );
    }

    const seatBtn = count > 12 ? 16 : count > 8 ? 18 : 22;
    const surface = Math.max(70, Math.min(118, 56 + count * 3.4));
    const rad = surface / 2 + seatBtn / 2 + 3;
    const outer = surface + (seatBtn + 14) * 2;

    return (
      <div
        className="sab-builder-classic-table"
        style={{ width: outer, height: outer }}
      >
        <div
          className="sab-builder-classic-surface"
          style={{ width: surface, height: surface }}
        >
          <span>T{block.location}</span>
        </div>
        {Array.from({ length: count }, (_, i) => {
          const angleDeg = (360 / count) * i - 90;
          const angleRad = (angleDeg * Math.PI) / 180;
          return (
            <span
              key={i}
              className="sab-builder-classic-seat is-table sa-layout-seat is-empty"
              style={{
                width: seatBtn,
                height: seatBtn,
                fontSize: seatBtn <= 16 ? 9 : 10,
                left: `calc(50% + ${Math.cos(angleRad) * rad}px)`,
                top: `calc(50% + ${Math.sin(angleRad) * rad}px)`,
              }}
            >
              {i + 1}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="sab-custom-builder">
      <div className="sab-custom-builder-head">
        <div>
          <span className="sab-custom-kicker">Custom layout builder</span>
          <strong>Place stage, rows &amp; tables</strong>
          <p className="sab-custom-sub mb-0">
            Drag to arrange · pink guides snap edges, centers &amp; equal spacing
            to the stage and other blocks
            {allowRows ? ` · up to ${maxRows} rows` : ""}
            {allowTables ? ` · up to ${maxTables} tables` : ""}
            {effectiveNeed > 0 ? ` · ${effectiveNeed} RSVP’d guests` : ""}
          </p>
        </div>
        <button
          type="button"
          className="sab-style-picker-close"
          aria-label="Close builder"
          onClick={onCancel}
        >
          ×
        </button>
      </div>

      <div className="sab-custom-toolbar">
        <label className="sab-builder-field is-name">
          <span>Style name</span>
          <input
            type="text"
            className="form-control sab-custom-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Mandap courtyard"
            maxLength={60}
          />
        </label>

        <label className="sab-builder-field is-stage-type">
          <span>Stage type</span>
          <select
            className="form-select sab-builder-select"
            value={stage ? stageType : ""}
            onChange={(e) => {
              const next = e.target.value;
              if (!next) {
                setStage(null);
                setSelectedId(null);
                return;
              }
              applyStagePreset(next);
            }}
          >
            <option value="">No stage</option>
            {STAGE_PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.label}
              </option>
            ))}
          </select>
        </label>

        <div className="sab-custom-tools">
          <button
            type="button"
            className="btn sab-custom-tool"
            onClick={ensureStage}
          >
            {stage ? "Reset stage" : "Add stage"}
          </button>

          {allowRows ? (
            <div className="sab-builder-add-group">
              <label className="sab-builder-field">
                <span>Seats / row</span>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={pendingRowSeats}
                  onChange={(e) => setPendingRowSeats(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn sab-custom-tool is-primary"
                onClick={addRowBlock}
              >
                Add row
                <em>
                  {usedRowLocations.size}/{maxRows}
                </em>
              </button>
            </div>
          ) : null}

          {allowTables ? (
            <div className="sab-builder-add-group">
              <label className="sab-builder-field">
                <span>Seats / table</span>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={pendingTableSeats}
                  onChange={(e) => setPendingTableSeats(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn sab-custom-tool is-primary"
                onClick={addTableBlock}
              >
                Add table
                <em>
                  {usedTableLocations.size}/{maxTables}
                </em>
              </button>
            </div>
          ) : null}

          <button
            type="button"
            className="btn sab-custom-tool is-danger"
            disabled={!selectedId}
            onClick={removeSelected}
          >
            Remove selected
          </button>
        </div>
      </div>

      {selectedBlock ? (
        <div className="sab-builder-selected">
          <span>
            Selected: {selectedBlock.kind === "row" ? "Row" : "Table"}{" "}
            {selectedBlock.location}
          </span>
          <label className="sab-builder-field is-inline">
            <span>Seat count</span>
            <input
              type="number"
              min="1"
              max="60"
              value={selectedBlock.seatCount}
              onChange={(e) => updateSelectedSeatCount(e.target.value)}
            />
          </label>
        </div>
      ) : null}

      <div className="sab-custom-meters">
        <div className="sab-custom-meter">
          <span>Placed seats</span>
          <strong>
            {placedSeats}
            <em> / ~{occasionCap || "—"}</em>
          </strong>
        </div>
        <div
          className={`sab-custom-meter${
            effectiveNeed > placedSeats ? " is-warn" : ""
          }`}
        >
          <span>RSVP’d guests</span>
          <strong>{effectiveNeed}</strong>
        </div>
        <div className="sab-builder-zoom sab-zoom-controls">
          <button
            type="button"
            className="sab-zoom-btn"
            title="Zoom out"
            onClick={() => adjustZoom(-0.1)}
          >
            <i className="bi bi-zoom-out" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="sab-zoom-label"
            title="Reset zoom"
            onClick={() => setZoom(1)}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="sab-zoom-btn"
            title="Zoom in"
            onClick={() => adjustZoom(0.1)}
          >
            <i className="bi bi-zoom-in" aria-hidden="true" />
          </button>
        </div>
      </div>

      {error ? <div className="sab-custom-error">{error}</div> : null}

      <div className="sab-builder-floor-shell" onWheel={onFloorWheel}>
        <div className="sab-zoom-viewport sab-builder-zoom-viewport">
          <div
            className="sab-zoom-content"
            style={{ transform: `scale(${zoom})` }}
          >
            <div
              className="sab-custom-floor is-builder"
              ref={floorRef}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              {alignGuides.map((guide, idx) => (
                <div
                  key={`${guide.axis}-${guide.pos}-${idx}`}
                  className={`sab-align-guide is-${guide.axis} is-${guide.kind || "edge"}`}
                  style={
                    guide.axis === "v"
                      ? { left: `${guide.pos}%` }
                      : { top: `${guide.pos}%` }
                  }
                />
              ))}
              {stage ? (
                <div
                  className={`sab-custom-stage is-${stage.type || stageType}${
                    selectedId === "stage" ? " is-selected" : ""
                  }`}
                  style={{
                    left: `${stage.x}%`,
                    top: `${stage.y}%`,
                    width: `${stage.w}%`,
                    height: `${stage.h}%`,
                  }}
                  onPointerDown={(e) => onPointerDownBlock(e, "stage")}
                >
                  <button
                    type="button"
                    className="sab-block-remove"
                    title="Remove stage"
                    aria-label="Remove stage"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBlock("stage");
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    ×
                  </button>
                  {stage.label || "Stage"}
                </div>
              ) : null}

              {blocks.map((block) => (
                <div
                  key={block.id}
                  className={`sab-custom-block is-${block.kind} has-preview is-classic-preview${
                    selectedId === block.id ? " is-selected" : ""
                  }`}
                  style={{
                    left: `${block.x}%`,
                    top: `${block.y}%`,
                    width: "auto",
                    height: "auto",
                  }}
                  onPointerDown={(e) => onPointerDownBlock(e, block.id)}
                >
                  <button
                    type="button"
                    className="sab-block-remove"
                    title={`Remove ${block.kind} ${block.location}`}
                    aria-label={`Remove ${block.kind} ${block.location}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBlock(block.id);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    ×
                  </button>
                  {renderSeatPreview(block)}
                </div>
              ))}

              {!stage && blocks.length === 0 ? (
                <div className="sab-custom-floor-empty">
                  Choose a stage type, then add rows or tables. Drag to arrange.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="sab-custom-actions">
        <button
          type="button"
          className="btn sab-custom-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn sab-custom-draft"
          disabled={saving}
          onClick={handleSaveDraft}
        >
          {saving ? "Saving…" : "Save as draft"}
        </button>
        <button
          type="button"
          className="btn btn-primary sab-save-btn"
          disabled={saving}
          onClick={handleSave}
        >
          {saving
            ? "Saving…"
            : initialStyle?.status === "draft"
              ? "Publish style"
              : "Save custom style"}
        </button>
      </div>
    </div>
  );
};

export default CustomSeatingBuilder;
